import { useQueryClient } from '@tanstack/react-query'

import { api } from '@/shared/lib/api'
import { showErrorAlert } from '@/shared/lib/errors'

import type { CreatePostParams } from './types'

export const useCreatePost = (eventSlug: string) => {
  const queryClient = useQueryClient()

  const createPost = async (params: CreatePostParams) => {
    const { data } = await api.post(
      `/user/digital_products/${eventSlug}/event_posts`,
      { record: params }
    )
    queryClient.invalidateQueries({ queryKey: [`/user/digital_products/${eventSlug}/event_posts`] })
    return data.record
  }

  return { createPost }
}

export const useToggleReaction = (eventSlug: string, postId: number) => {
  const queryClient = useQueryClient()

  const toggleReaction = async (emoji: string) => {
    try {
      const { data, status } = await api.post(
        `/user/digital_products/${eventSlug}/event_posts/${postId}/reactions`,
        { record: { emoji } },
        { validateStatus: (s) => s < 500 }
      )
      queryClient.invalidateQueries({ queryKey: [`/user/digital_products/${eventSlug}/event_posts`] })
      return status === 204 ? null : data.record
    } catch (err) {
      showErrorAlert(err)
    }
  }

  return { toggleReaction }
}

export const useCreateComment = (eventSlug: string, postId: number) => {
  const queryClient = useQueryClient()

  const createComment = async (content: string) => {
    const { data } = await api.post(
      `/user/digital_products/${eventSlug}/event_posts/${postId}/comments`,
      { record: { content } }
    )
    queryClient.invalidateQueries({
      queryKey: [`/user/digital_products/${eventSlug}/event_posts/${postId}/comments`]
    })
    return data.record
  }

  return { createComment }
}

export const useSharePost = (eventSlug: string, postId: number) => {
  const sharePost = async () => {
    await api.post(`/user/digital_products/${eventSlug}/event_posts/${postId}/shares`)
    if (navigator.share) {
      navigator.share({ title: 'Compartilhar post', url: window.location.href }).catch(() => null)
    }
  }
  return { sharePost }
}

export const useReportPost = (eventSlug: string, postId: number) => {
  const reportPost = async () => {
    await api.post(`/user/digital_products/${eventSlug}/event_posts/${postId}/reports`)
  }
  return { reportPost }
}
